import React, {
  useState,
  useCallback,
  useMemo,
  useRef,
  FC,
  useEffect,
} from "react";
import Modal from "../Modal";
import { debounce } from "lodash";
import { PrimaryButton } from "../Buttons";
import { formatName } from "../../utils/format";
import { useAppSelector } from "../../store/hooks";
import {
  FormSearch,
  FormGroupSearch,
  SearchFooterButton,
  SearchErrorRow,
} from "../FormFields";
import {
  UserPlusIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/20/solid";
import {
  getAccount,
  loaderService,
  getFilteredAccounts,
  getFilteredBoxAccounts,
  getFavoriteAndHistoricalLocations,
} from "../../services";
import {
  normalizeSearchText,
  serverQueryStillCovers,
  serverSearchTokens,
  stripSearchPunctuation,
  toServerQueryForm,
  SEARCH_MAX_LENGTH,
  SEARCH_STRINGS,
  emptyMessage,
  hintMessage,
} from "../../utils/searchText";
import { TaxIdentificationTypeInterface } from "../../interfaces/TaxIdentificationTypeInterface";
import {
  AccountInterface,
  LocationInterface,
  BoxAccountInterface,
  BusinessUnitInterface,
  RecurrentDestinationInterface,
  DeliveryType,
} from "../../interfaces";
import { CheckBadgeIcon } from "@heroicons/react/24/outline";
import classNames from "classnames";
import {
  useAccountSearch,
  UseAccountSearch,
  LIVE_SEARCH_DEBOUNCE_MS,
} from "../../hooks/useAccountSearch";

/**
 * Texto de la fila de ayuda del desplegable. Es texto plano: el enlace a la
 * búsqueda avanzada llega con WP-T2, junto con el modal que lo atiende.
 */
const dropdownHint = (search: UseAccountSearch): string => {
  if (search.hint !== "None") {
    return hintMessage(search.hint);
  }

  const belowMinimum =
    search.normalized.route === "BelowMinimum" || search.status === "BelowMinimum";

  return belowMinimum && search.normalized.raw !== ""
    ? SEARCH_STRINGS.belowMinimum
    : "";
};

const dropdownErrorRow = (search: UseAccountSearch): SearchErrorRow | undefined =>
  search.error ? { text: search.error, onRetry: search.searchNow } : undefined;

export const ClientOption: FC<AccountInterface> = ({
  accountFullName,
  listAccountPhone,
  abreviationName,
  identificationNumber,
  agreementID,
  listAuthorizingAccount,
}: AccountInterface) => {
  const isEnterprise = !!agreementID || listAuthorizingAccount?.length > 0;

  return (
    <div className="flex items-center">
      <div className="flex flex-col">
        <div className="flex items-center text-sm font-semibold leading-5 truncate">
          {formatName(accountFullName)}

          <CheckBadgeIcon
            title="Cliente Crédito Corporativo"
            data-te-toggle="tooltip"
            className={classNames(
              "ml-2 inline h-5 w-5 text-indigo-600 group-hover:text-white",
              !isEnterprise && "hidden"
            )}
          />
        </div>

        {!listAccountPhone ||
        listAccountPhone.length === 0 ||
        (abreviationName !== "V-" && abreviationName !== "E-") ? (
          <p className="text-sm leading-5">
            {abreviationName}
            {identificationNumber}
          </p>
        ) : (
          <p className="text-sm leading-5">
            {!!listAccountPhone &&
              listAccountPhone.length > 0 &&
              listAccountPhone[0].phoneNumber}
          </p>
        )}
      </div>
    </div>
  );
};

const LocationOption: FC<RecurrentDestinationInterface> = ({
  deliveryTypeID,
  consigneeFullName,
  consigneeIdentificationType,
  consigneeIdentificationNumber,
  consigneePhone,
  buCode,
  buAreaName,
  addressName,
  addressLine1,
  addressLandMark,
  postalCode,
  displayName,
}) => {
  const taxIdentificationTypes = useAppSelector(
    (state) => state.inmutable.taxIdentificationTypes
  );

  const abreviationName = useMemo(() => {
    return taxIdentificationTypes.find(
      (type) => type.taxIdentificationTypeId === consigneeIdentificationType
    )?.abreviationName;
  }, [consigneeIdentificationType, taxIdentificationTypes]);

  return (
    <div className="flex items-center">
      <div className="flex flex-col w-full">
        <p
          className={classNames(
            "text-sm font-semibold leading-5 truncate text-indigo-600 group-hover:text-white"
          )}
        >
          {displayName}
        </p>
        <p className="text-sm font-semibold leading-5 truncate">
          {formatName(consigneeFullName)}
        </p>
        <p className="text-sm leading-5">
          {abreviationName}
          {consigneeIdentificationNumber}
        </p>
        <p className="text-sm leading-5">{consigneePhone}</p>

        {deliveryTypeID === DeliveryType.AT_OFFICE && (
          <p className="text-sm leading-5">
            {buCode} - {buAreaName}
          </p>
        )}

        {deliveryTypeID === DeliveryType.AT_HOME && (
          <p className="leading-5 truncate">
            {addressName}
            <br />
            <span className="text-xs">
              {addressLine1} {postalCode && `(código postal: ${postalCode})`}
            </span>
            <br />
            <span className="text-xs">{addressLandMark}</span>
          </p>
        )}
      </div>
    </div>
  );
};

interface BoxClientOptionProps {
  boxAccount: BoxAccountInterface;
}
const BoxClientOption: FC<BoxClientOptionProps> = ({ boxAccount }) => {
  return (
    <div className="flex items-center">
      <div className="flex flex-col">
        <p className="text-sm font-semibold leading-5 truncate">
          Casillero {boxAccount.boxAccountCode}
        </p>
        <p className="text-sm font-semibold leading-5 truncate">
          {boxAccount.accountFullName}
        </p>
        <p className="text-sm leading-5">
          {boxAccount.accountAbreviationName}
          {boxAccount.accountIdentificationNumber}
        </p>
      </div>
    </div>
  );
};

const AddClient: FC<{ onClick: React.MouseEventHandler<HTMLDivElement> }> = ({
  onClick,
}) => {
  return (
    <div className="flex flex-1 py-3 items-center opacity-75" onClick={onClick}>
      <UserPlusIcon className="h-5 w-5 mr-2 shrink-0" />

      <p className="text-sm truncate">Crear nuevo</p>
    </div>
  );
};

/** A non-interactive trailer row (no matches, "y N más"). */
const TrailerRow: FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="px-4 py-3 text-sm text-gray-500">{children}</p>
);

/** A clickable trailer row (retry, whole-book search). */
const TrailerButton: FC<{
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ label, onClick, children }) => (
  <button
    type="button"
    aria-label={label}
    // Keep the caret in the input: a native button takes DOM focus on click and
    // the teller would have to click back into the box before typing again.
    onMouseDown={(event) => event.preventDefault()}
    onClick={onClick}
    className="w-full px-4 py-3 text-left text-sm font-medium text-indigo-600 hover:bg-indigo-50"
  >
    {children}
  </button>
);

/**
 * Filter consignee-book rows client-side. Both the query and every field value
 * go through the same normaliser, so the match is accent- and case-insensitive.
 * Every whitespace-separated token must hit at least one field, which keeps the
 * order-independent behaviour of the older `filterDestination`.
 *
 * Identification and phone are compared with punctuation stripped from BOTH
 * sides, so the "V-12345678" on screen matches "v-12345678", "V12345678" and
 * "12.345.678". Address fields are deliberately not searchable.
 */
export const filterRecurrentDestination = (
  rows: RecurrentDestinationInterface[],
  search: string,
  taxIdTypes: TaxIdentificationTypeInterface[]
): RecurrentDestinationInterface[] => {
  // Any whitespace, not only U+0020: a name pasted from Excel arrives with a
  // tab between the surname and the given name and would become one token.
  const tokens = normalizeSearchText(search)
    .split(/\s+/)
    .filter((token) => token !== "");

  if (tokens.length === 0) {
    // A copy, never the caller's array: this function is exported and the
    // consignee-book caller sorts what it gets back.
    return [...rows];
  }

  return rows.filter((row) => {
    const abreviationName =
      taxIdTypes.find(
        (type) =>
          type.taxIdentificationTypeId === row.consigneeIdentificationType
      )?.abreviationName ?? "";

    const plainFields = [
      row.displayName ?? "",
      row.consigneeFullName ?? "",
      row.consigneeEmail ?? "",
    ].map(normalizeSearchText);

    const punctuationFields = [
      `${abreviationName}${row.consigneeIdentificationNumber ?? ""}`,
      row.consigneePhone ?? "",
    ].map((field) => stripSearchPunctuation(normalizeSearchText(field)));

    return tokens.every((token) => {
      if (plainFields.some((field) => field.includes(token))) {
        return true;
      }

      const strippedToken = stripSearchPunctuation(token);
      // A token made only of punctuation ("-") must not match every row.
      if (strippedToken === "") {
        return false;
      }

      return punctuationFields.some((field) => field.includes(strippedToken));
    });
  });
};

/**
 * Favoritos are ordered by the name the shipper gave the destination, ascending
 * and locale-aware. Nulls sort last: `?? ""` would put them first.
 */
export const compareFavoriteDestinations = (
  a: RecurrentDestinationInterface,
  b: RecurrentDestinationInterface
): number => {
  if (a.displayName == null) return b.displayName == null ? 0 : 1;
  if (b.displayName == null) return -1;

  return a.displayName.localeCompare(b.displayName, "es", {
    sensitivity: "base",
  });
};
// Filter client-destination by search
export const filterDestination = (
  search: string,
  client?: AccountInterface,
  location?: LocationInterface
) => {
  const criteria = search.toLowerCase();
  // split by tokens separated by spaces
  const tokens = criteria
    .split(" ")
    .filter((token) => token !== "")
    .map((token) => token.toLowerCase());
  if (tokens.length === 0) {
    return true;
  }
  // filter by tokens
  return tokens.every((token) =>
    filterByTokenDestination(token, client, location)
  );
};
// Filter client-destination by token
export const filterByTokenDestination = (
  token: string,
  client?: AccountInterface,
  location?: LocationInterface
) => {
  return (
    location?.name.toLowerCase().includes(token) ||
    location?.address.toLowerCase().includes(token) ||
    client?.accountFullName.toLowerCase().includes(token) ||
    ((client?.abreviationName ?? "") + client?.identificationNumber)
      .toLowerCase()
      .includes(token) ||
    (!!client?.listAccountPhone &&
      client?.listAccountPhone.some((phone) =>
        phone.phoneNumber.toLowerCase().includes(token)
      ))
  );
};

interface AccountIdentification {
  abreviationName: string;
  identificationNumber: string;
}
interface AccountSearchProps {
  title: string;
  selected?: AccountInterface;
  disabled?: boolean;
  error?: string;
  placeholder?: string;
  hideCreate?: boolean;
  ignoredAccounts?: AccountIdentification[];
  onChange?: (search: string) => void;
  onSelectClient: (option: AccountInterface) => void;
  openCreationModal: (open: boolean) => void;
}
export const AccountSearch: FC<AccountSearchProps> = ({
  title,
  selected,
  disabled = false,
  error,
  placeholder = SEARCH_STRINGS.placeholder,
  hideCreate = false,
  ignoredAccounts = [],
  onChange = () => {},
  onSelectClient,
  openCreationModal,
}) => {
  const user = useAppSelector((state) => state.user.user);
  const [currentError, setCurrentError] = useState(error);

  const isCreationHidden = useMemo(() => {
    return hideCreate || user?.roleName === "Consulta Innovus";
  }, [hideCreate, user?.roleName]);

  // Se aplica al pintar, no al guardar: la caché de sesión conserva lo que
  // devolvió el API y cada consumidor esconde lo suyo.
  const notIgnored = (client: AccountInterface) =>
    !ignoredAccounts.some(
      (ignored) =>
        ignored.abreviationName === client.abreviationName &&
        ignored.identificationNumber === client.identificationNumber
    );

  const search = useAccountSearch({ filter: notIgnored });
  const { setQuery, searchNow, query, normalized } = search;

  const handleFocusChange = useCallback(
    (focus: boolean) => {
      if (!focus && selected) {
        setQuery(formatName(selected.accountFullName), { silent: true });
      }
    },
    [selected, setQuery]
  );

  const FooterRows = useCallback(
    () => (
      <SearchFooterButton onClick={() => openCreationModal(true)}>
        <UserPlusIcon className="h-5 w-5 mr-2 shrink-0" />
        <span className="truncate">Crear nuevo</span>
      </SearchFooterButton>
    ),
    [openCreationModal]
  );

  useEffect(() => {
    setQuery(formatName(selected?.accountFullName ?? ""), { silent: true });
    // Sólo cuando cambia el cliente elegido: `setQuery` cambia de identidad
    // cuando el API enseña su mínimo, y no debe pisar lo que se está escribiendo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  useEffect(() => {
    onChange(query);
  }, [query, onChange]);

  useEffect(() => {
    setCurrentError(error);
  }, [error]);

  // Por debajo del mínimo no hay lista ni "Crear nuevo": la fila de ayuda dice
  // qué falta para buscar.
  const showFooter =
    !isCreationHidden && normalized.route !== "BelowMinimum";

  return (
    <div className="flex flex-1 flex-col">
      <div className="relative w-full">
        <FormSearch
          value={query}
          id={`input${title}`}
          label={title}
          loader={search.loading}
          pending={search.pending}
          options={search.results}
          disabled={disabled}
          error={currentError}
          placeholder={placeholder}
          maxLength={SEARCH_MAX_LENGTH}
          hint={dropdownHint(search)}
          errorRow={dropdownErrorRow(search)}
          notFound={emptyMessage(query)}
          onChange={(event) => {
            setCurrentError("");
            setQuery(event.target.value);
          }}
          onEnter={searchNow}
          onSelectOption={(option) => {
            if (!option) {
              return;
            }
            onSelectClient(option);
            setQuery(formatName(option.accountFullName ?? ""), { silent: true });
          }}
          onChangeFocus={handleFocusChange}
          RenderOption={({ option }) => <ClientOption {...option} />}
          FooterRows={showFooter ? FooterRows : undefined}
        />
      </div>
    </div>
  );
};

interface BoxAccountSearchProps {
  title?: string;
  selected?: BoxAccountInterface;
  shipperAccount?: AccountInterface;
  error?: string;
  onChange?: (search: string) => void;
  setBoxAccount: (
    boxAccount: BoxAccountInterface,
    client: AccountInterface,
    businessUnit: BusinessUnitInterface
  ) => void;
}
export const BoxAccountSearch: FC<BoxAccountSearchProps> = ({
  title,
  selected,
  shipperAccount,
  error,
  onChange = () => {},
  setBoxAccount,
}) => {
  const [search, setSearch] = useState("");
  const [loader, setLoader] = useState(false);
  const [currentError, setCurrentError] = useState(error);
  const [openErrorModal, setOpenErrorModal] = useState(false);
  const businessUnits = useAppSelector(
    (state) => state.inmutable.businessUnits
  );
  const [boxAccounts, setBoxAccounts] = useState<BoxAccountInterface[]>([]);
  const [notFound, setNotFound] = useState("Código de casillero inválido");

  const getBoxAccountsData = useCallback(
    async (boxCode: string) => {
      if (boxCode.length < 2) {
        setBoxAccounts([]);
        return;
      }

      setLoader(true);
      const boxes = await getFilteredBoxAccounts(boxCode, businessUnits);
      if (!boxes.didError && boxes.model) {
        setBoxAccounts(boxes.model);
      } else {
        setCurrentError(boxes.errorMessage);
      }
      setLoader(false);
    },
    [businessUnits]
  );

  const debouncedSearch = useMemo(
    () => debounce(getBoxAccountsData, 500),
    [getBoxAccountsData]
  );

  const searchCallback = useCallback(
    (boxCode: string) => debouncedSearch(boxCode),
    [debouncedSearch]
  );

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    setCurrentError("");
    setSearch(inputValue);
    searchCallback(inputValue);
  };

  const handleSelect = async (boxAccount: BoxAccountInterface) => {
    if (
      !!boxAccount.authorizedShippers &&
      boxAccount.authorizedShippers.length > 0
    ) {
      const foundShipper = boxAccount.authorizedShippers.find(
        (account) => account.accountId === shipperAccount?.id
      );
      if (!foundShipper) {
        setBoxAccounts([]);
        setNotFound(
          "El cliente no está autorizado a enviar al casillero " +
            boxAccount.boxAccountCode
        );
        setOpenErrorModal(true);
        return;
      }
    }

    loaderService.start();
    const account = await getAccount(boxAccount.accountID);
    loaderService.stop();
    if (!account) {
      return;
    }

    setBoxAccount(boxAccount, account, boxAccount.businessUnit);
  };

  useEffect(() => {
    setSearch(selected?.boxAccountCode ?? "");
  }, [selected]);

  useEffect(() => {
    onChange(search);
  }, [search, onChange]);

  useEffect(() => {
    setCurrentError(error);
  }, [error]);

  return (
    <div className="flex flex-1">
      <div className="relative w-full" id={`input-casillero`}>
        <FormGroupSearch
          value={search}
          label={title}
          error={currentError}
          optionGroups={[
            {
              loader,
              title: "Casilleros",
              options: boxAccounts,
            },
          ]}
          notFound={notFound}
          disabled={!shipperAccount}
          onChange={handleChange}
          placeholder="Buscar casillero..."
          onSelectOption={(option) => handleSelect(option)}
          onChangeFocus={(focus) => {
            if (!focus && selected) setSearch(selected.boxAccountCode);
          }}
          RenderOption={({ option }) => <BoxClientOption boxAccount={option} />}
        />
      </div>

      <Modal openModal={openErrorModal} setOpenModal={setOpenErrorModal}>
        <div
          className="flex flex-col items-center justify-center"
          style={{ maxWidth: "25rem" }}
        >
          <div className="flex flex-col items-center justify-center w-full">
            <ExclamationTriangleIcon className="w-32 h-32" />
          </div>
          <p className="mt-2 text-xl text-center text-gray-900">{notFound}</p>
          <div className="mt-4 flex flex-row justify-center gap-4">
            <PrimaryButton
              className="px-4"
              onClick={() => setOpenErrorModal(false)}
            >
              Aceptar
            </PrimaryButton>
          </div>
        </div>
      </Modal>
    </div>
  );
};

interface AccountSearchFavoriteHistoricProps extends AccountSearchProps {
  shipperId?: string;
  autoCompleteOff?: boolean;
  onSelectDestination: (destination: RecurrentDestinationInterface) => void;
}
/** How many rows of each group the dropdown shows before the "y N más" row. */
const VISIBLE_CAP = 5;

type BookState = "idle" | "loading" | "loaded" | "failed";

export const AccountSearchFavoriteHistoric: FC<
  AccountSearchFavoriteHistoricProps
> = ({
  title,
  error,
  selected,
  disabled,
  shipperId,
  placeholder,
  autoCompleteOff,
  onSelectClient,
  onSelectDestination,
  openCreationModal,
}) => {
  const [search, setSearch] = useState("");
  const [clients, setClients] = useState<AccountInterface[]>([]);
  const [loader, setLoader] = useState(false);
  const [clientsError, setClientsError] = useState(false);
  const [currentError, setCurrentError] = useState(error);
  const [timerId, setTimerId] = useState<NodeJS.Timeout | null>(null);

  // The shipper's consignee book, fetched once per shipper and cached in the
  // service. `baseState` drives every visible state of the two book groups.
  const [rawRows, setRawRows] = useState<RecurrentDestinationInterface[]>([]);
  const [baseState, setBaseState] = useState<BookState>(
    shipperId ? "loading" : "idle"
  );

  // Result of the optional whole-book server search, kept only while the text
  // in the box still refines the query it was fetched with.
  const [serverRows, setServerRows] = useState<
    RecurrentDestinationInterface[] | null
  >(null);
  const [serverQuery, setServerQuery] = useState("");
  const [serverState, setServerState] = useState<BookState>("idle");

  // Sequence numbers replace the old by-value `canceled` flag, which was read
  // at call time and therefore never cancelled anything: shipper A's rows could
  // land under shipper B, leaking one customer's consignees to another.
  const seqRef = useRef(0);
  const serverSeqRef = useRef(0);
  const serverRawQueryRef = useRef("");
  const searchRef = useRef(search);
  const prevFocusedRef = useRef(false);
  const retryArmedRef = useRef(false);

  const taxIdTypes = useAppSelector(
    (state) => state.inmutable.taxIdentificationTypes
  );

  useEffect(() => {
    searchRef.current = search;
  }, [search]);

  const getClientsData = async (criteria: string) => {
    // The empty check comes first: `setLoader(true)` before an early return
    // left the Clientes group spinning forever whenever the box was empty.
    if (criteria.trim() === "") {
      setClients([]);
      setClientsError(false);
      setLoader(false);
      return;
    }

    setLoader(true);
    const clients = await getFilteredAccounts(criteria);
    if (!clients?.didError && !!clients?.model) {
      setClients(clients.model);
      setClientsError(false);
    } else {
      setClients([]);
      setClientsError(true);
      setCurrentError(clients?.errorMessage);
    }
    setLoader(false);
  };

  // Handle search by debouncing the event
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    setCurrentError("");
    setClientsError(false);
    setSearch(inputValue);

    if (inputValue.trim() === "") {
      // Synchronously, so the Clientes group disappears with the keystroke
      // instead of one debounce later.
      setClients([]);
      setLoader(false);
    } else {
      setLoader(true);
    }
  };

  /**
   * One request per shipper, always with an empty `SearchInput`: that is the
   * cached query, and `search` holds the selected consignee's name whenever
   * this runs again.
   */
  const getLocations = useCallback(async () => {
    seqRef.current += 1;
    serverSeqRef.current += 1;
    const seq = seqRef.current;

    setRawRows([]);
    setServerRows(null);
    setServerQuery("");
    setServerState("idle");

    if (!shipperId) {
      setBaseState("idle");
      return;
    }

    setBaseState("loading");

    const response = await getFavoriteAndHistoricalLocations(shipperId, "");

    if (seq !== seqRef.current) return;

    if (!response || response.didError || !response.model) {
      setBaseState("failed");
      retryArmedRef.current = true;
      return;
    }

    setRawRows(response.model);
    setBaseState("loaded");
    retryArmedRef.current = false;
  }, [shipperId]);

  const dropServerSearch = useCallback(() => {
    serverSeqRef.current += 1;
    serverRawQueryRef.current = "";
    setServerRows(null);
    setServerQuery("");
    setServerState("idle");
  }, []);

  const runServerSearch = useCallback(
    async (rawQuery: string) => {
      if (!shipperId) return;

      const sentQuery = toServerQueryForm(rawQuery);
      // Same bar as `canServerSearch`: with no token the procedure survives,
      // it just stops filtering and hands back the capped book we already have.
      if (serverSearchTokens(sentQuery).length === 0) return;

      serverSeqRef.current += 1;
      const serverSeq = serverSeqRef.current;
      const seq = seqRef.current;

      serverRawQueryRef.current = rawQuery;
      setServerQuery(sentQuery);
      setServerRows(null);
      setServerState("loading");

      const response = await getFavoriteAndHistoricalLocations(
        shipperId,
        rawQuery
      );

      // Discard when a newer server search, a shipper switch or a drop site
      // (blur, select, a query that no longer refines this one) already won.
      if (serverSeq !== serverSeqRef.current || seq !== seqRef.current) return;
      // Compare against the value captured in this closure, never the state:
      // a drop may already have cleared it.
      if (!serverQueryStillCovers(sentQuery, toServerQueryForm(searchRef.current)))
        return;

      if (!response || response.didError || !response.model) {
        setServerRows(null);
        setServerState("failed");
        return;
      }

      setServerRows(response.model);
      setServerState("loaded");
    },
    [shipperId]
  );

  useEffect(() => {
    setSearch(formatName(selected?.accountFullName ?? ""));
  }, [selected]);

  useEffect(() => {
    setCurrentError(error);
  }, [error]);

  useEffect(() => {
    if (timerId) {
      clearTimeout(timerId);
    }

    const newTimerId = setTimeout(() => {
      getClientsData(search);
    }, 1000);

    setTimerId(newTimerId);
  }, [search]);

  // The only fetch of the book. `taxIdTypes` was a dead dependency: nothing
  // here reads it, and the catalog is re-dispatched on every page load.
  useEffect(() => {
    getLocations();

    return () => {
      // Bumping on unmount stops a late response from touching dead state.
      seqRef.current += 1;
    };
  }, [getLocations]);

  // Keep or drop the server result as the query moves.
  useEffect(() => {
    if (serverState === "idle") return;

    const current = toServerQueryForm(search);

    if (serverState === "failed") {
      // Any edit clears the error row and re-offers the Enter trailer.
      if (current !== serverQuery) dropServerSearch();
      return;
    }

    // Not containment: the procedure ORs its tokens, so a query that gains one
    // is a WIDER search than the one we hold, and keeping the old answer would
    // hide rows the server would now return. Only a token being extended keeps
    // the result sound; anything else drops it and re-offers the trailer.
    if (search.trim() === "" || !serverQueryStillCovers(serverQuery, current)) {
      dropServerSearch();
    }
  }, [search, serverState, serverQuery, dropServerSearch]);

  /**
   * The base the dropdown filters. Once a server search has landed it is the
   * union of both sets, deduplicated per group: the server matches whole words
   * (FULLTEXT CONTAINS) while this filter matches substrings, so the server
   * result is not a superset of what the teller could already see.
   */
  const baseRows = useMemo(() => {
    if (!serverRows) return rawRows;

    // `consigneeAccountID` carries the key: `addressID` alone is read nowhere
    // else in this app, so its uniqueness is unverified, and an AT_OFFICE row
    // plausibly carries the destination store's address id — shared by every
    // consignee who collects there. Keyed on the address alone, the whole
    // server result for that store would be discarded as "already seen".
    const keyOf = (row: RecurrentDestinationInterface) =>
      `${row.tipoAdr}|${row.consigneeAccountID}|${row.deliveryTypeID}|${row.addressID}`;

    const seen = new Set(rawRows.map(keyOf));

    return [
      ...rawRows,
      ...serverRows.filter((row) => {
        const key = keyOf(row);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      }),
    ];
  }, [rawRows, serverRows]);

  const favoriteMatches = useMemo(
    () =>
      filterRecurrentDestination(
        baseRows.filter((row) => row.tipoAdr === "Favorito"),
        search,
        taxIdTypes
      ).sort(compareFavoriteDestinations),
    [baseRows, search, taxIdTypes]
  );

  // Recientes keeps the server's order, which is recency.
  const historicMatches = useMemo(
    () =>
      filterRecurrentDestination(
        baseRows.filter((row) => row.tipoAdr === "Sales"),
        search,
        taxIdTypes
      ),
    [baseRows, search, taxIdTypes]
  );

  const totalMatches = favoriteMatches.length + historicMatches.length;

  // Both book groups spin together, whichever request is in flight.
  const bookLoading = baseState === "loading" || serverState === "loading";

  /**
   * One predicate for the Enter key and the trailer button alike.
   *
   * Deliberately does NOT test whether the book came back at its cap. It used
   * to: `favorites >= 50 || recents >= 50` over the delivered rows. But the
   * procedure applies TOP 50 at intermediate stages and then groups and unions,
   * so a truncated book arrives UNDER the threshold — measured 2026-09-13, a
   * real shipper whose book holds at least 201 consignees delivers 49 rows, and
   * the trailer that would have reached the other 152 never rendered.
   *
   * Fewer than five local matches is the signal that actually matters: the
   * teller cannot find the consignee in front of them. When the book really is
   * complete the server answers with rows we already hold and the trailer
   * resolves to "No hay más coincidencias en su libreta", which is a true
   * answer rather than a wasted one.
   */
  const canServerSearch =
    baseState === "loaded" &&
    serverState === "idle" &&
    // Not just "non-empty": the procedure drops one-character tokens, and with
    // nothing left it abandons the search — returning zero rows for a query
    // that reached it, which would read as a false "not in your book".
    serverSearchTokens(toServerQueryForm(search)).length > 0 &&
    totalMatches < VISIBLE_CAP;

  const handleEnter = (event: React.KeyboardEvent<HTMLInputElement>) => {
    // Nothing here submits anything (no form wraps this widget), but keep the
    // guard so a future form cannot turn Enter into a page reload.
    event.preventDefault();

    if (serverState === "failed") {
      runServerSearch(serverRawQueryRef.current);
      return;
    }

    if (!canServerSearch) return;

    runServerSearch(search);
  };

  const handleChangeFocus = useCallback(
    (focus: boolean) => {
      const wasFocused = prevFocusedRef.current;
      prevFocusedRef.current = focus;

      if (!focus) {
        setSearch(formatName(selected?.accountFullName ?? ""));
        dropServerSearch();
        return;
      }

      // FormGroupSearch re-runs this callback on every render, so only a real
      // false -> true edge counts, and the armed flag makes it once per
      // failure. Without both, a failing backend would loop refetching.
      if (!wasFocused && baseState === "failed" && retryArmedRef.current) {
        retryArmedRef.current = false;
        getLocations();
      }
    },
    [baseState, dropServerSearch, getLocations, selected]
  );

  const favoritesTrailer = useMemo(() => {
    if (bookLoading) return undefined;

    if (baseState === "failed") {
      return (
        <TrailerButton
          label="Reintentar la carga de su libreta"
          onClick={() => getLocations()}
        >
          No se pudo cargar su libreta. Toque para reintentar.
        </TrailerButton>
      );
    }

    if (baseState !== "loaded") return undefined;

    if (favoriteMatches.length === 0) {
      // With an empty box an empty group simply does not render.
      return search.trim() === "" ? undefined : (
        <TrailerRow>Sin coincidencias</TrailerRow>
      );
    }

    if (favoriteMatches.length > VISIBLE_CAP) {
      return (
        <TrailerRow>
          y {favoriteMatches.length - VISIBLE_CAP} más, siga escribiendo
        </TrailerRow>
      );
    }

    return undefined;
  }, [bookLoading, baseState, favoriteMatches.length, search, getLocations]);

  const recentTrailer = useMemo(() => {
    if (bookLoading || baseState !== "loaded") return undefined;

    const serverReturnedNothing =
      serverState === "loaded" && !!serverRows && serverRows.length === 0;

    let serverRow: JSX.Element | undefined;
    if (serverState === "failed") {
      serverRow = (
        <TrailerButton
          key="server-error"
          label="Reintentar la búsqueda en su libreta"
          onClick={() => runServerSearch(serverRawQueryRef.current)}
        >
          No se pudo buscar en su libreta. Toque para reintentar.
        </TrailerButton>
      );
    } else if (serverReturnedNothing) {
      serverRow =
        totalMatches === 0 ? (
          <TrailerRow key="server-empty">
            Sin coincidencias en su libreta
          </TrailerRow>
        ) : (
          <TrailerRow key="server-no-more">
            No hay más coincidencias en su libreta
          </TrailerRow>
        );
    } else if (canServerSearch) {
      serverRow = (
        <TrailerButton
          key="server-search"
          label="Buscar en toda su libreta"
          onClick={() => runServerSearch(search)}
        >
          Presione Enter para buscar en toda su libreta
        </TrailerButton>
      );
    }

    const rows: JSX.Element[] = [];

    if (historicMatches.length === 0) {
      // A zero-result server row already says this; do not say it twice.
      if (search.trim() !== "" && !serverReturnedNothing) {
        rows.push(<TrailerRow key="none">Sin coincidencias</TrailerRow>);
      }
    } else if (historicMatches.length > VISIBLE_CAP) {
      rows.push(
        <TrailerRow key="more">
          y {historicMatches.length - VISIBLE_CAP} más, siga escribiendo
        </TrailerRow>
      );
    }

    if (serverRow) rows.push(serverRow);

    return rows.length > 0 ? <>{rows}</> : undefined;
  }, [
    bookLoading,
    baseState,
    serverState,
    serverRows,
    totalMatches,
    canServerSearch,
    historicMatches.length,
    search,
    runServerSearch,
  ]);

  const clientsTrailer = useMemo(() => {
    if (search.trim() === "" || loader || clientsError) return undefined;
    if (clients.length > 0) return undefined;

    return <TrailerRow>Elementos no encontrados</TrailerRow>;
  }, [search, loader, clientsError, clients.length]);

  return (
    <div className="flex flex-1 flex-col">
      <div className="relative w-full">
        <FormGroupSearch<RecurrentDestinationInterface | AccountInterface>
          value={search}
          id={`search-favorite-historic-account-${title}`}
          label={title}
          disabled={disabled}
          error={currentError}
          placeholder={placeholder}
          showLastOptionWhenEmpty
          optionGroups={[
            {
              loader: bookLoading,
              title: "Favoritos",
              options: favoriteMatches.slice(0, VISIBLE_CAP),
              trailer: favoritesTrailer,
            },
            {
              loader: bookLoading,
              title: "Recientes",
              options: historicMatches.slice(0, VISIBLE_CAP),
              trailer: recentTrailer,
            },
            {
              // Derived, like `options` below it: blur resets `search` without
              // going through `handleChange`, which is the only place that
              // clears `loader`, so a stored-only flag leaves the group
              // spinning over an empty box until the debounce catches up.
              loader: search.trim() !== "" && loader,
              title: "Clientes",
              options: search.trim() === "" ? [] : clients,
              trailer: clientsTrailer,
            },
          ]}
          onChange={handleChange}
          onEnter={handleEnter}
          onSelectOption={(option) => {
            if (!option) return;

            // Synchronously, so the server result never survives a selection.
            dropServerSearch();

            if ("tipoAdr" in option) {
              setSearch(option.consigneeFullName);
              onSelectDestination(option);
            } else {
              setSearch(option.accountFullName);
              onSelectClient(option);
            }
          }}
          onChangeFocus={handleChangeFocus}
          RenderOption={({ option }) => {
            if ("tipoAdr" in option) {
              return <LocationOption {...option} />;
            } else {
              return <ClientOption {...option} />;
            }
          }}
          FooterRows={() => (
            <AddClient onClick={() => openCreationModal(true)} />
          )}
          autoComplete="off"
        />
      </div>
    </div>
  );
};
